import useBusinessCategories from '../useBusinessCategories';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import { FiCamera, FiX, FiEdit, FiTrash2, FiPlus, FiCheckCircle, FiAlertTriangle, FiFileText, FiUpload } from 'react-icons/fi';
import './EditBusiness.css';
import useImageCrop from '../components/useImageCrop';


const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const isValidTimeFormat = (time) => /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
const isEndAfterStart = (start, end) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return eh * 60 + em > sh * 60 + sm;
};
const getTimeSlotsByDay = (timeSlots) => {
  const byDay = {};
  DAYS_OF_WEEK.forEach(day => { byDay[day] = timeSlots.filter(s => s.day === day); });
  return byDay;
};

const MAX_COMPANY_DOCUMENTS = 4;
const MAX_DOCUMENT_SIZE_MB = 10;

export default function EditBusiness() {
  const { openCrop, cropDialog } = useImageCrop();
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const db = getFirestore();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [business, setBusiness] = useState(null);
  // True when an admin is editing a business they don't own (e.g. finishing its setup).
  const [editingAsAdmin, setEditingAsAdmin] = useState(false);
  const exitPath = editingAsAdmin ? '/admin/businesses' : '/dashboard';

  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const categoryOptions = useBusinessCategories(category);
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [businessHours, setBusinessHours] = useState('');
  const [status, setStatus] = useState('active');

  const [profileImage, setProfileImage] = useState(null); // existing URL or File
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]); // [{url?, file?, preview}]
  const [companyDocuments, setCompanyDocuments] = useState([]); // [{url?, file?, name}]

  const [services, setServices] = useState([]);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState('');
  const [newServiceDuration, setNewServiceDuration] = useState('');
  const [newServiceDescription, setNewServiceDescription] = useState('');
  const [serviceTimeSlots, setServiceTimeSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [editingTimeSlotIndex, setEditingTimeSlotIndex] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'businesses', businessId));
        if (!snap.exists()) {
          setError('Business not found.');
          setLoading(false);
          return;
        }
        const data = snap.data();
        if (data.ownerId !== currentUser?.uid) {
          const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
          if (!userSnap.exists() || userSnap.data().is_admin !== true) {
            setError('You do not have access to edit this business.');
            setLoading(false);
            return;
          }
          setEditingAsAdmin(true);
        }
        setBusiness(data);
        setBusinessName(data.businessName || '');
        setDescription(data.description || '');
        setCategory(data.category || '');
        setAddress(data.address || '');
        setPhone(data.phone || '');
        setEmail(data.email || '');
        setWebsite(data.website || '');
        setBusinessHours(data.businessHours || '');
        setStatus(data.status || 'active');
        setProfileImage(data.profileImageUrl || null);
        setProfileImagePreview(data.profileImageUrl || null);
        setGalleryImages((data.additionalImageUrls || []).map(url => ({ url, preview: url })));
        setGalleryImages((data.additionalImageUrls || []).map(url => ({ url, preview: url })));
        setCompanyDocuments((data.companyDocumentUrls || []).map(url => ({
          url,
          name: decodeURIComponent(url.split('/').pop().split('?')[0].split('_').slice(0, -1).join('_')) || 'Document',
        })));
        setServices(data.services || []);
        setServices(data.services || []);
      } catch (err) {
        console.error('Error loading business:', err);
        setError(`Couldn't load business: ${err.code || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) load();
  }, [businessId, currentUser, db]);

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    openCrop(file, applyProfileCrop);
  };

  const applyProfileCrop = (file, preview) => {
    setProfileImage(file);
    setProfileImagePreview(preview);
  };

  const handleAddGalleryImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    openCrop(file, (cropped, preview) => {
      setGalleryImages(prev => [...prev, { file: cropped, preview }]);
    }, 1);
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddDocument = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (companyDocuments.length >= MAX_COMPANY_DOCUMENTS) {
      setError(`You can only have up to ${MAX_COMPANY_DOCUMENTS} documents.`);
      e.target.value = '';
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setError('Documents must be PDF files.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
      setError(`Each document must be under ${MAX_DOCUMENT_SIZE_MB}MB.`);
      e.target.value = '';
      return;
    }

    setError('');
    setCompanyDocuments(prev => [...prev, { file, name: file.name }]);
    e.target.value = '';
  };

  const removeDocument = (index) => {
    setCompanyDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // --- Time slots (within the service being added/edited) ---
  const resetTimeSlotForm = () => {
    setSelectedDay('Monday');
    setStartTime('09:00');
    setEndTime('17:00');
    setEditingTimeSlotIndex(null);
  };

  const addTimeSlotToService = () => {
    if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
      setError('Use HH:MM format for times (e.g. 09:00).');
      return;
    }
    if (!isEndAfterStart(startTime, endTime)) {
      setError('End time must be after start time.');
      return;
    }
    const slotData = {
      id: editingTimeSlotIndex !== null ? serviceTimeSlots[editingTimeSlotIndex].id : Date.now().toString(),
      day: selectedDay, startTime, endTime,
    };
    let updated;
    if (editingTimeSlotIndex !== null) {
      updated = [...serviceTimeSlots];
      updated[editingTimeSlotIndex] = slotData;
    } else {
      const overlap = serviceTimeSlots.find(s =>
        s.day === selectedDay &&
        ((startTime >= s.startTime && startTime < s.endTime) ||
         (endTime > s.startTime && endTime <= s.endTime) ||
         (startTime <= s.startTime && endTime >= s.endTime))
      );
      if (overlap) {
        setError('This time slot overlaps with an existing one on the same day.');
        return;
      }
      updated = [...serviceTimeSlots, slotData];
    }
    updated.sort((a, b) => {
      const dayDiff = DAYS_OF_WEEK.indexOf(a.day) - DAYS_OF_WEEK.indexOf(b.day);
      return dayDiff !== 0 ? dayDiff : a.startTime.localeCompare(b.startTime);
    });
    setServiceTimeSlots(updated);
    setError('');
    resetTimeSlotForm();
  };

  const editTimeSlot = (index) => {
    const slot = serviceTimeSlots[index];
    setSelectedDay(slot.day);
    setStartTime(slot.startTime);
    setEndTime(slot.endTime);
    setEditingTimeSlotIndex(index);
  };

  const deleteTimeSlot = (index) => {
    setServiceTimeSlots(prev => prev.filter((_, i) => i !== index));
  };

  // --- Services ---
  const resetServiceForm = () => {
    setNewServiceName(''); setNewServicePrice(''); setNewServiceDuration('');
    setNewServiceDescription(''); setServiceTimeSlots([]);
    setEditingServiceIndex(null); resetTimeSlotForm();
  };

  const addOrUpdateService = () => {
    if (!newServiceName || !newServicePrice || !newServiceDuration) {
      setError('Please fill in service name, price, and duration.');
      return;
    }
    if (serviceTimeSlots.length === 0) {
      setError('Add at least one time slot for this service.');
      return;
    }
    const isEditingExisting = editingServiceIndex !== null && editingServiceIndex >= 0;
    const serviceData = {
      id: isEditingExisting ? services[editingServiceIndex].id : Date.now().toString(),
      name: newServiceName,
      price: parseFloat(newServicePrice),
      duration: parseInt(newServiceDuration, 10),
      description: newServiceDescription,
      timeSlots: serviceTimeSlots,
    };
    if (isEditingExisting) {
      setServices(prev => prev.map((s, i) => (i === editingServiceIndex ? serviceData : s)));
    } else {
      setServices(prev => [...prev, serviceData]);
    }
    setError('');
    resetServiceForm();
  };

  const editService = (index) => {
    if (editingServiceIndex === index) { resetServiceForm(); return; }
    const s = services[index];
    setNewServiceName(s.name);
    setNewServicePrice(String(s.price));
    setNewServiceDuration(String(s.duration));
    setNewServiceDescription(s.description || '');
    setServiceTimeSlots(s.timeSlots || []);
    setEditingServiceIndex(index);
    resetTimeSlotForm();
  };

  const deleteService = (index) => {
    setServices(prev => prev.filter((_, i) => i !== index));
    if (editingServiceIndex === index) resetServiceForm();
  };

  const uploadImage = async (file, path) => {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleSave = async () => {
    if (!businessName || !description || !category || !address) {
      setError('Please fill in business name, description, category and address.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      let profileImageUrl = business.profileImageUrl || null;
      if (profileImage instanceof File) {
        try {
          profileImageUrl = await uploadImage(profileImage, `businesses/${businessId}/profile_${Date.now()}.jpg`);
        } catch (err) {
          console.error('Profile image upload failed:', err);
          setError('Profile image upload failed. Saving the rest of your changes without it.');
        }
      }

      const additionalImageUrls = [];
      for (const img of galleryImages) {
        if (img.url) {
          additionalImageUrls.push(img.url);
        } else if (img.file) {
          try {
            const url = await uploadImage(img.file, `businesses/${businessId}/gallery_${Date.now()}.jpg`);
            additionalImageUrls.push(url);
          } catch (err) {
            console.error('Gallery image upload failed:', err);
          }
        }
      }

      const companyDocumentUrls = [];
      for (const docItem of companyDocuments) {
        if (docItem.url) {
          companyDocumentUrls.push(docItem.url);
        } else if (docItem.file) {
          try {
            const url = await uploadImage(docItem.file, `businesses/${businessId}/documents/company_doc_${Date.now()}.pdf`);
            companyDocumentUrls.push(url);
          } catch (err) {
            console.error('Document upload failed:', err);
            setError(`Couldn't upload ${docItem.name}. Other changes will still be saved.`);
          }
        }
      }

      await updateDoc(doc(db, 'businesses', businessId), {
        businessName, description, category, address, phone, email, website,
        businessHours, status, profileImageUrl, additionalImageUrls, companyDocumentUrls, services,
        updatedAt: serverTimestamp(),
        ...(editingAsAdmin && { lastEditedByAdmin: currentUser.uid, lastEditedByAdminAt: serverTimestamp() }),
      });

      navigate(exitPath);
      } catch (err) {
        console.error('Error updating business:', err);
        setError(`Couldn't save changes: ${err.code || err.message}`);
      } finally {
        setSubmitting(false);
      }
    };

  if (loading) return <div className="edit-business-page">Loading...</div>;
  if (error && !business) return <div className="edit-business-page"><div className="form-error"><FiAlertTriangle /> {error}</div></div>;

  return (
    <div className="edit-business-page">
      <div className="edit-business-container">
        <h1 className="edit-business-title">Edit Business</h1>
        {editingAsAdmin && (
          <div className="admin-editing-banner">
            You're editing this business as an admin. Changes are saved on the owner's behalf.
          </div>
        )}

        {error && <div className="form-error"><FiAlertTriangle /> {error}</div>}

        {/* Profile image */}
        <div className="edit-section">
          <label>Profile Image</label>
          <div className="image-upload-row">
            {profileImagePreview ? (
              <div className="image-preview-wrapper">
                <img src={profileImagePreview} alt="Profile" className="logo-preview" />
                <button type="button" className="business-image-crop-button" onClick={() => openCrop(profileImage, applyProfileCrop)}>Crop</button>
                <label className="change-image-overlay">
                  <FiCamera />
                  <input type="file" accept="image/*" onChange={handleProfileImageChange} hidden />
                </label>
              </div>
            ) : (
              <label className="image-picker-placeholder">
                <FiCamera /><span>Add Photo</span>
                <input type="file" accept="image/*" onChange={handleProfileImageChange} hidden />
              </label>
            )}
          </div>
        </div>

        {/* Business info */}
        <div className="edit-section">
          <h2>Business Information</h2>
          <label>Business Name *</label>
          <input value={businessName} onChange={e => setBusinessName(e.target.value)} />

          <label>Description *</label>
          <textarea rows={4} value={description} onChange={e => setDescription(e.target.value)} />

          <label>Category *</label>
          {categoryOptions.error && <p role="alert">{categoryOptions.error} <button type="button" onClick={categoryOptions.refresh}>Retry categories</button></p>}
          <select value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">Select a category</option>
            {categoryOptions.categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>

          <label>Address *</label>
          <input value={address} onChange={e => setAddress(e.target.value)} />

          <label>Phone Number</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} />

          <label>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} />

          <label>Website</label>
          <input value={website} onChange={e => setWebsite(e.target.value)} />

          <label>Business Hours</label>
          <input
            value={businessHours}
            onChange={e => setBusinessHours(e.target.value)}
            placeholder="e.g. Mon-Fri: 9AM-5PM, Sat: 10AM-3PM"
          />

          <label>Status</label>
          <div className="status-toggle">
            <button
              type="button"
              className={`status-option ${status === 'active' ? 'selected' : ''}`}
              onClick={() => setStatus('active')}
            >
              Active
            </button>
            <button
              type="button"
              className={`status-option ${status === 'inactive' ? 'selected' : ''}`}
              onClick={() => setStatus('inactive')}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Services */}
        <div className="edit-section">
          <h2>Services & Availability</h2>
          <p className="section-subtext">Each service has its own availability schedule.</p>

          {services.length > 0 && (
            <div className="services-edit-list">
              {services.map((service, index) => (
                <div key={service.id} className="service-edit-item">
                  <div className="service-edit-header">
                    <div>
                      <strong>{service.name}</strong>
                      <span className="service-meta"> · R{service.price} · {service.duration} mins</span>
                    </div>
                    <div className="service-edit-actions">
                      <button className="icon-btn" onClick={() => editService(index)}><FiEdit /></button>
                      <button className="icon-btn danger" onClick={() => deleteService(index)}><FiTrash2 /></button>
                    </div>
                  </div>
                  {service.timeSlots?.length > 0 && (
                    <div className="service-slots-preview">
                      {Object.entries(getTimeSlotsByDay(service.timeSlots)).map(([day, slots]) =>
                        slots.length > 0 && (
                          <span key={day} className="slot-chip">
                            {day.slice(0, 3)}: {slots.map(s => `${s.startTime}-${s.endTime}`).join(', ')}
                          </span>
                        )
                      )}
                    </div>
                  )}

                  {editingServiceIndex === index && (
                    <ServiceForm
                      {...{
                        newServiceName, setNewServiceName, newServicePrice, setNewServicePrice,
                        newServiceDuration, setNewServiceDuration, newServiceDescription, setNewServiceDescription,
                        serviceTimeSlots, selectedDay, setSelectedDay, startTime, setStartTime, endTime, setEndTime,
                        editingTimeSlotIndex, addTimeSlotToService, editTimeSlot, deleteTimeSlot, resetTimeSlotForm,
                        addOrUpdateService, resetServiceForm, editingServiceIndex,
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {editingServiceIndex === null && (
            <div className="service-add-block">
              <button className="btn-secondary" onClick={() => setEditingServiceIndex(-1)}>
                <FiPlus /> Add Service
              </button>
            </div>
          )}
          {editingServiceIndex === -1 && (
            <ServiceForm
              {...{
                newServiceName, setNewServiceName, newServicePrice, setNewServicePrice,
                newServiceDuration, setNewServiceDuration, newServiceDescription, setNewServiceDescription,
                serviceTimeSlots, selectedDay, setSelectedDay, startTime, setStartTime, endTime, setEndTime,
                editingTimeSlotIndex, addTimeSlotToService, editTimeSlot, deleteTimeSlot, resetTimeSlotForm,
                addOrUpdateService, resetServiceForm, editingServiceIndex,
              }}
            />
          )}
        </div>

        {/* Gallery */}
        <div className="edit-section">
          <h2>Gallery Images</h2>
          <div className="additional-images-row">
            {galleryImages.map((img, index) => (
              <div key={index} className="image-preview-wrapper small">
                <button type="button" className="business-image-crop-button" aria-label={`Crop gallery image ${index + 1}`}
                  onClick={() => openCrop(img.file || img.url, (file, preview) => setGalleryImages(prev => prev.map((item, i) => i === index ? { file, preview } : item)), 1)}>Crop</button>
                <img src={img.preview} alt={`Gallery ${index + 1}`} className="logo-preview small" />
                <button className="remove-image-btn" onClick={() => removeGalleryImage(index)}><FiX /></button>
              </div>
            ))}
            <label className="image-picker-placeholder small">
              <FiCamera />
              <input type="file" accept="image/*" onChange={handleAddGalleryImage} hidden />
            </label>
          </div>
        </div>

        {/* Company Documents */}
        <div className="edit-section">
          <h2>Company Documents</h2>
          <p className="section-subtext">PDF documents used for verification. Up to {MAX_COMPANY_DOCUMENTS} files.</p>
          <div className="document-upload-row">
            {companyDocuments.map((docItem, index) => (
              <div key={index} className="document-preview">
                <FiFileText />
                <span className="document-name">{docItem.name}</span>
                <button className="remove-image-btn" onClick={() => removeDocument(index)}>
                  <FiX />
                </button>
              </div>
            ))}
            {companyDocuments.length < MAX_COMPANY_DOCUMENTS && (
              <label className="document-picker-placeholder">
                <FiUpload />
                <span>Upload PDF ({companyDocuments.length}/{MAX_COMPANY_DOCUMENTS})</span>
                <input type="file" accept="application/pdf,.pdf" onChange={handleAddDocument} hidden />
              </label>
            )}
          </div>
        </div>

        <div className="step-actions">
          <button className="btn-secondary" onClick={() => navigate(exitPath)} disabled={submitting}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={submitting || Boolean(cropDialog)}>
            {submitting ? 'Saving...' : (<><FiCheckCircle /> Save Changes</>)}
          </button>
        </div>
      </div>
      {cropDialog}
    </div>
  );
}

// Extracted so the "add" and "edit" service forms don't duplicate this block
function ServiceForm({
  newServiceName, setNewServiceName, newServicePrice, setNewServicePrice,
  newServiceDuration, setNewServiceDuration, newServiceDescription, setNewServiceDescription,
  serviceTimeSlots, selectedDay, setSelectedDay, startTime, setStartTime, endTime, setEndTime,
  editingTimeSlotIndex, addTimeSlotToService, editTimeSlot, deleteTimeSlot, resetTimeSlotForm,
  addOrUpdateService, resetServiceForm, editingServiceIndex,
}) {
  return (
    <div className="service-form">
      <label>Service Name *</label>
      <input value={newServiceName} onChange={e => setNewServiceName(e.target.value)} placeholder="e.g. Haircut" />

      <label>Price (R) *</label>
      <input type="number" value={newServicePrice} onChange={e => setNewServicePrice(e.target.value)} />

      <label>Duration (minutes) *</label>
      <input type="number" value={newServiceDuration} onChange={e => setNewServiceDuration(e.target.value)} />

      <label>Description</label>
      <textarea rows={2} value={newServiceDescription} onChange={e => setNewServiceDescription(e.target.value)} />

      <div className="time-slot-section">
        <label>Availability for this service *</label>

        {serviceTimeSlots.length > 0 && (
          <div className="time-slot-list">
            {serviceTimeSlots.map((slot, index) => (
              <div key={slot.id} className="time-slot-item">
                <span>{slot.day.slice(0, 3)}: {slot.startTime}–{slot.endTime}</span>
                <div>
                  <button className="icon-btn" onClick={() => editTimeSlot(index)}><FiEdit /></button>
                  <button className="icon-btn danger" onClick={() => deleteTimeSlot(index)}><FiTrash2 /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="day-selector">
          {DAYS_OF_WEEK.map(day => (
            <button
              key={day}
              type="button"
              className={`day-chip ${selectedDay === day ? 'selected' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              {day.slice(0, 3)}
            </button>
          ))}
        </div>

        <div className="time-input-row">
          <div>
            <label>Start</label>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div>
            <label>End</label>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        <button className="btn-secondary small" onClick={addTimeSlotToService}>
          {editingTimeSlotIndex !== null ? 'Update Time Slot' : 'Add Time Slot'}
        </button>
        {editingTimeSlotIndex !== null && (
          <button className="btn-secondary small" onClick={resetTimeSlotForm}>Cancel</button>
        )}
      </div>

      <div className="service-form-actions">
        <button className="btn-primary small" onClick={addOrUpdateService}>
          {editingServiceIndex !== null && editingServiceIndex !== -1 ? 'Update Service' : 'Add Service'}
        </button>
        <button className="btn-secondary small" onClick={resetServiceForm}>Cancel</button>
      </div>
    </div>
  );
}
